import Phaser from 'phaser';
import { TILE, GRID_W, GRID_H, CANVAS_W, CANVAS_H, PlayerId, HumanPlayerId, PlayerStats, DEFAULT_STATS, CONTROLS } from '../types';
import { getLevel, getNextLevel, isBossLevel } from '../config/levels';
import { Block } from '../entities/Block';
import { Player, PlayerKeys } from '../entities/Player';
import { Bomb } from '../entities/Bomb';
import { ExplosionTile, ExplosionOrientation } from '../entities/Explosion';
import { BaseEnemy } from '../entities/enemies/BaseEnemy';
import { Wanderer } from '../entities/enemies/Wanderer';
import { Tracker } from '../entities/enemies/Tracker';
import { SmartTracker } from '../entities/enemies/SmartTracker';
import { BombDodger } from '../entities/enemies/BombDodger';
import { Boss, BOSS_SPECS, BossAttackKind } from '../entities/enemies/Boss';
import { pickEnemyKinds, EnemyKind } from '../config/enemies';
import { PowerUp } from '../entities/PowerUp';
import { pickRandomPowerUp, POWERUP_DROP_CHANCE, POWERUPS } from '../config/powerups';
import { saveAutosave, clearAutosave, saveHighScore } from '../systems/SaveManager';
import { AudioManager } from '../systems/AudioManager';
import type { ContinueOverlay } from './ContinueOverlay';
import type { HudState } from './HUDScene';

type Cell = 'hard' | 'soft' | 'empty' | 'bomb';

const RESPAWN_DELAY_MS = 1200;       // auto-respawn after death (when lives remain)
const CONTINUE_REFILL_LIVES = 3;      // Y on continue prompt restores this many lives

export interface PlayerCarry {
  name: string;
  stats: PlayerStats;
  lives: number;
  score: number;
}

export interface GameSceneData {
  world: number;
  stage: number;
  p1: PlayerCarry;
  p2: PlayerCarry;
}

export const DEFAULT_GAME_DATA: GameSceneData = {
  world: 1,
  stage: 1,
  p1: { name: 'GEO',  stats: { ...DEFAULT_STATS }, lives: 3, score: 0 },
  p2: { name: 'ARIS', stats: { ...DEFAULT_STATS }, lives: 3, score: 0 },
};

export class GameScene extends Phaser.Scene {
  private grid: Cell[][] = [];
  private blockSprites: Map<string, Block> = new Map();
  private blocksGroup!: Phaser.Physics.Arcade.StaticGroup;
  private bombsGroup!: Phaser.Physics.Arcade.StaticGroup;
  private explosionsGroup!: Phaser.Physics.Arcade.StaticGroup;
  private bombsByCell: Map<string, Bomb> = new Map();
  private remoteBombQueues: Map<PlayerId, Bomb[]> = new Map();
  private players: Player[] = [];
  private enemies: BaseEnemy[] = [];
  private enemiesGroup!: Phaser.Physics.Arcade.Group;
  private powerupsGroup!: Phaser.Physics.Arcade.Group;
  private scoresById: Record<string, number> = { p1: 0, p2: 0 };
  private spectatorById: Record<string, boolean> = { p1: false, p2: false };
  private pendingContinueById: Record<string, boolean> = { p1: false, p2: false };
  private currentWorld = 1;
  private currentStage = 1;
  private levelEnded = false;
  private boss: Boss | null = null;
  private bossHpBar?: Phaser.GameObjects.Graphics;
  private levelTimeLimitSec = 0;
  private levelStartedAt = 0;

  constructor() {
    super({ key: 'GameScene' });
  }

  create(data: GameSceneData) {
    this.cameras.main.setBackgroundColor('#131120');
    this.physics.world.setBounds(0, 0, CANVAS_W, CANVAS_H);

    // Reset per-scene state (Phaser re-uses scene instances on restart)
    this.bombsByCell.clear();
    this.remoteBombQueues.clear();
    this.blockSprites.clear();
    this.players = [];
    this.enemies = [];
    this.levelEnded = false;
    this.spectatorById = { p1: false, p2: false };
    this.pendingContinueById = { p1: false, p2: false };

    this.blocksGroup = this.physics.add.staticGroup();
    this.bombsGroup = this.physics.add.staticGroup();
    this.explosionsGroup = this.physics.add.staticGroup();
    this.enemiesGroup = this.physics.add.group();
    this.powerupsGroup = this.physics.add.group();

    const payload = data && data.world ? data : DEFAULT_GAME_DATA;
    this.currentWorld = payload.world;
    this.currentStage = payload.stage;
    this.scoresById = { p1: payload.p1.score, p2: payload.p2.score };

    // Switch BGM to this world's chiptune loop
    AudioManager.startBgmForWorld(this.currentWorld);

    const level = getLevel(this.currentWorld, this.currentStage);
    this.buildLevel(level.layout);
    this.levelTimeLimitSec = level.timeLimit;
    this.levelStartedAt = this.time.now;

    const kb = this.input.keyboard!;
    // Prevent Tab from shifting browser focus while playing
    kb.addCapture('TAB');
    const keysP1: PlayerKeys = {
      up: kb.addKey(CONTROLS.p1.up),
      down: kb.addKey(CONTROLS.p1.down),
      left: kb.addKey(CONTROLS.p1.left),
      right: kb.addKey(CONTROLS.p1.right),
      plant: kb.addKey(CONTROLS.p1.plant),
      remote: kb.addKey(CONTROLS.p1.remote),
    };
    const keysP2: PlayerKeys = {
      up: kb.addKey(CONTROLS.p2.up),
      down: kb.addKey(CONTROLS.p2.down),
      left: kb.addKey(CONTROLS.p2.left),
      right: kb.addKey(CONTROLS.p2.right),
      plant: kb.addKey(CONTROLS.p2.plant),
      remote: kb.addKey(CONTROLS.p2.remote),
    };

    const p1Spawn = this.findCellInLayout(level.layout, '1');
    const p2Spawn = this.findCellInLayout(level.layout, '2');

    const p1 = new Player(
      this, 'p1', payload.p1.name, p1Spawn.col, p1Spawn.row, keysP1,
      () => this.tryPlantBomb('p1'),
      () => this.remoteDetonate('p1'),
      () => this.tryRespawn('p1'),
    );
    p1.stats = { ...payload.p1.stats };
    p1.lives = payload.p1.lives;
    const p2 = new Player(
      this, 'p2', payload.p2.name, p2Spawn.col, p2Spawn.row, keysP2,
      () => this.tryPlantBomb('p2'),
      () => this.remoteDetonate('p2'),
      () => this.tryRespawn('p2'),
    );
    p2.stats = { ...payload.p2.stats };
    p2.lives = payload.p2.lives;
    this.players = [p1, p2];

    // ESC = pause
    this.input.keyboard!.on('keydown-ESC', () => {
      if (this.levelEnded) return;
      this.scene.pause();
      this.scene.launch('PauseScene', { snapshot: this.snapshotForSave() });
    });
    // M = global mute toggle
    this.input.keyboard!.on('keydown-M', () => {
      AudioManager.toggleMute();
    });

    // Provide a grid query both players (corner slide) and enemies use.
    const gridQuery = {
      isBlocked: (col: number, row: number) => {
        if (col < 0 || col >= GRID_W || row < 0 || row >= GRID_H) return true;
        const cell = this.grid[row][col];
        // Bombs do NOT count as blocked for the player's lane query
        // (otherwise you can't escape from a tile you just bombed).
        return cell === 'hard' || cell === 'soft';
      },
    };
    p1.attachGrid(gridQuery);
    p2.attachGrid(gridQuery);

    this.boss = null;
    this.bossHpBar?.destroy();
    this.bossHpBar = undefined;

    if (isBossLevel(this.currentWorld, this.currentStage)) {
      this.spawnBoss();
    } else {
      this.spawnEnemies(level.enemyCount);
    }

    // Collisions
    this.physics.add.collider(this.players, this.blocksGroup);
    this.physics.add.collider(this.players, this.bombsGroup);
    this.physics.add.collider(this.enemiesGroup, this.blocksGroup);
    this.physics.add.collider(this.enemiesGroup, this.bombsGroup);
    this.physics.add.overlap(this.players, this.explosionsGroup, (pObj, eObj) => {
      const p = pObj as Player;
      if (!p.alive) return;
      const e = eObj as ExplosionTile;
      // Friendly fire OFF: a player is immune to flames from THEIR OWN bombs?
      // No — classic Bomberman: you DIE from your own bomb. But you don't die
      // from the OTHER player's bomb. So: damage only when explosion owner != player id.
      // Boss flames (ownerId='boss') damage all players; player bombs only damage own owner
      if (e.ownerId !== p.id && e.ownerId !== 'boss') return;
      // Cover rule: if less than 40% of the player body is inside the flame
      // body, the player is mostly behind a wall/block — no damage.
      const coverageOK = this.flameCoverageEnoughToHit(p, e);
      if (!coverageOK) return;
      if (p.takeDamage()) {
        AudioManager.sfx('die');
        this.handlePlayerDeath(p);
      } else {
        AudioManager.sfx('hit');
      }
    });
    this.physics.add.overlap(this.enemiesGroup, this.explosionsGroup, (obj) => {
      const e = obj as BaseEnemy;
      if (!e.alive) return;
      // Boss: only damages in vulnerable state; mini-enemies die instantly
      if (e instanceof Boss) {
        const hit = e.tryHit();
        if (hit && e.alive) AudioManager.sfx('boss-hit');
        if (!e.alive) {
          AudioManager.sfx('boss-die');
          this.enemies = this.enemies.filter(x => x !== e);
          this.scoresById.p1 += 5000;
          this.scoresById.p2 += 5000;
        }
      } else {
        AudioManager.sfx('enemy-die');
        e.kill();
        this.enemies = this.enemies.filter(x => x !== e);
        this.scoresById.p1 += 200;
        this.scoresById.p2 += 200;
      }
    });
    this.physics.add.overlap(this.players, this.enemiesGroup, (pObj, _eObj) => {
      const p = pObj as Player;
      if (p.alive && p.takeDamage()) {
        this.handlePlayerDeath(p);
      }
    });
    this.physics.add.overlap(this.players, this.powerupsGroup, (pObj, puObj) => {
      const p = pObj as Player;
      const pu = puObj as PowerUp;
      if (!p.alive) return;
      pu.spec.apply(
        p.stats,
        () => { p.lives = Math.min(9, p.lives + 1); },
        (n: number) => { this.scoresById[p.id] += n; },
      );
      this.scoresById[p.id] += 100;
      AudioManager.sfx('powerup');
      pu.destroy();
    });
    // Powerups should not be blocked by explosions (they shouldn't despawn easily)
    // but should be blown away by explosion → destroy on overlap
    this.physics.add.overlap(this.powerupsGroup, this.explosionsGroup, (puObj) => {
      const pu = puObj as PowerUp;
      pu.destroy();
    });

    // Launch the always-on overlay scenes (HUD + per-player continue prompt).
    // `launch` keeps GameScene running while the overlays render on top.
    this.scene.launch('HUDScene');
    this.scene.launch('ContinueOverlay');
    this.scene.bringToTop('HUDScene');
    this.scene.bringToTop('ContinueOverlay');

    // Tear them down deterministically when GameScene shuts down (level
    // transition, return to menu, etc.) so they don't leak state.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      const overlay = this.scene.get('ContinueOverlay') as ContinueOverlay | undefined;
      overlay?.cancelFor('p1');
      overlay?.cancelFor('p2');
      this.scene.stop('HUDScene');
      this.scene.stop('ContinueOverlay');
    });
  }

  update(time: number, delta: number) {
    for (const p of this.players) p.update(time, delta);
    for (const e of this.enemies) e.update(time, delta);

    if (this.boss && this.bossHpBar) this.drawBossHpBar();

    if (!this.levelEnded) {
      const enemiesAlive = this.enemies.some(e => e.alive);
      if (!enemiesAlive) {
        this.completeLevel();
      } else if (this.timeLeftSeconds() <= 0) {
        // Out of time: treat all non-spectator players as forfeit.
        this.gameOver('TIMP EXPIRAT');
      } else if (this.allPlayersOut()) {
        // Both players are spectator → game over (no chance of progress).
        this.gameOver('GAME OVER');
      }
    }
  }

  /** Read-only snapshot consumed by HUDScene each frame. */
  getHudState(): HudState {
    const enemiesLeft = this.enemies.filter(e => e.alive).length;
    return {
      world: this.currentWorld,
      stage: this.currentStage,
      isBoss: isBossLevel(this.currentWorld, this.currentStage),
      enemiesLeft,
      timeLeftSeconds: this.timeLeftSeconds(),
      timeLimitSeconds: this.levelTimeLimitSec,
      p1: this.playerHudState('p1'),
      p2: this.playerHudState('p2'),
    };
  }

  private playerHudState(id: PlayerId): HudState['p1'] {
    const p = this.players.find(pl => pl.id === id);
    if (!p) {
      return {
        id, name: id.toUpperCase(), lives: 0, score: this.scoresById[id] ?? 0,
        alive: false, spectator: true,
        bombCount: 0, bombRange: 0, speedLevel: 0,
        hasKick: false, hasPierce: false, hasShield: false, hasRemote: false,
      };
    }
    return {
      id,
      name: p.displayName,
      lives: Math.max(0, p.lives),
      score: this.scoresById[id] ?? 0,
      alive: p.alive,
      spectator: this.spectatorById[id],
      bombCount: p.stats.bombCount,
      bombRange: p.stats.bombRange,
      speedLevel: p.stats.speedLevel,
      hasKick: p.stats.hasKick,
      hasPierce: p.stats.hasPierce,
      hasShield: p.stats.hasShield,
      hasRemote: p.stats.hasRemote,
    };
  }

  private timeLeftSeconds(): number {
    const elapsed = (this.time.now - this.levelStartedAt) / 1000;
    return Math.max(0, this.levelTimeLimitSec - elapsed);
  }

  private allPlayersOut(): boolean {
    // With infinite lives, only explicit spectator status can take a player out.
    return this.players.every(p => this.spectatorById[p.id]);
  }

  private buildLevel(layout: string[]) {
    this.grid = Array.from({ length: GRID_H }, () => Array<Cell>(GRID_W).fill('empty'));
    for (let r = 0; r < GRID_H; r++) {
      for (let c = 0; c < GRID_W; c++) {
        const ch = layout[r][c];
        // Ground first
        const ground = this.add.image(c * TILE + TILE / 2, r * TILE + TILE / 2, 'tile-ground');
        ground.setDisplaySize(TILE, TILE);
        ground.setDepth(-1);

        if (ch === '#') {
          const b = new Block(this, c, r, 'hard');
          this.blocksGroup.add(b);
          this.blockSprites.set(this.k(c, r), b);
          this.grid[r][c] = 'hard';
        } else if (ch === 's') {
          const b = new Block(this, c, r, 'soft');
          this.blocksGroup.add(b);
          this.blockSprites.set(this.k(c, r), b);
          this.grid[r][c] = 'soft';
        }
      }
    }
  }

  private findCellInLayout(layout: string[], ch: string): { col: number; row: number } {
    for (let r = 0; r < layout.length; r++) {
      const idx = layout[r].indexOf(ch);
      if (idx >= 0) return { col: idx, row: r };
    }
    return { col: 1, row: 1 };
  }

  private k(c: number, r: number): string {
    return `${c},${r}`;
  }

  // ---- Bombs ----

  private countActiveBombs(ownerId: PlayerId): number {
    let n = 0;
    for (const b of this.bombsByCell.values()) if (b.ownerId === ownerId) n++;
    return n;
  }

  private tryPlantBomb(ownerId: PlayerId) {
    const player = this.players.find(p => p.id === ownerId)!;
    if (!player.alive) return;
    const col = player.gridCol();
    const row = player.gridRow();
    if (this.grid[row][col] !== 'empty') return;
    if (this.countActiveBombs(ownerId) >= player.stats.bombCount) return;

    const bomb = new Bomb(
      this, col, row, ownerId,
      player.stats.bombRange,
      player.stats.hasPierce,
      player.stats.hasRemote,
      (b) => this.detonate(b),
    );
    this.bombsGroup.add(bomb);
    this.bombsByCell.set(this.k(col, row), bomb);
    this.grid[row][col] = 'bomb';
    // Track planting order for sequential remote detonation
    const q = this.remoteBombQueues.get(ownerId) ?? [];
    q.push(bomb);
    this.remoteBombQueues.set(ownerId, q);
    AudioManager.sfx('plant');
  }

  private remoteDetonate(ownerId: PlayerId) {
    const q = this.remoteBombQueues.get(ownerId);
    if (!q || q.length === 0) return;
    // Detonate the oldest bomb (front of queue). Each press = one bomb.
    // Chain reactions from fire still trigger immediately via triggerChain().
    const bomb = q[0]; // will be spliced out in detonate()
    bomb.triggerChain();
  }

  private detonate(bomb: Bomb) {
    this.bombsByCell.delete(this.k(bomb.col, bomb.row));
    this.grid[bomb.row][bomb.col] = 'empty';
    // Remove from remote queue (may be absent if bomb expired on its own timer)
    const q = this.remoteBombQueues.get(bomb.ownerId);
    if (q) {
      const idx = q.indexOf(bomb);
      if (idx >= 0) q.splice(idx, 1);
    }
    bomb.destroy();
    AudioManager.sfx('explode');

    this.spawnExplosion(bomb.col, bomb.row, 'center', bomb.ownerId);

    const dirs: Array<[number, number, ExplosionOrientation]> = [
      [1, 0, 'horizontal'], [-1, 0, 'horizontal'],
      [0, 1, 'vertical'],   [0, -1, 'vertical'],
    ];
    for (const [dx, dy, orient] of dirs) {
      for (let i = 1; i <= bomb.range; i++) {
        const c = bomb.col + dx * i;
        const r = bomb.row + dy * i;
        if (c <= 0 || c >= GRID_W - 1 || r <= 0 || r >= GRID_H - 1) break;
        const cell = this.grid[r][c];
        if (cell === 'hard') break;
        if (cell === 'soft') {
          this.destroySoftBlock(c, r);
          this.spawnExplosion(c, r, orient, bomb.ownerId);
          if (!bomb.pierce) break;
          continue;
        }
        if (cell === 'bomb') {
          this.spawnExplosion(c, r, orient, bomb.ownerId);
          const chained = this.bombsByCell.get(this.k(c, r));
          if (chained) this.time.delayedCall(60, () => chained.triggerChain());
          if (!bomb.pierce) break;
          continue;
        }
        this.spawnExplosion(c, r, orient, bomb.ownerId);
      }
    }
  }

  private destroySoftBlock(c: number, r: number) {
    const b = this.blockSprites.get(this.k(c, r));
    if (b) {
      b.destroy();
      this.blockSprites.delete(this.k(c, r));
      AudioManager.sfx('block-break');
    }
    this.grid[r][c] = 'empty';
    if (Math.random() < POWERUP_DROP_CHANCE) {
      const spec = pickRandomPowerUp();
      // Spawn after the explosion at this tile fades — otherwise the
      // explosion->powerup overlap destroys it before anyone can see it.
      this.time.delayedCall(550, () => {
        if (this.grid[r]?.[c] !== 'empty') return;
        const pu = new PowerUp(this, c, r, spec);
        this.powerupsGroup.add(pu);
      });
    }
  }

  private spawnExplosion(c: number, r: number, orient: ExplosionOrientation, ownerId: PlayerId) {
    const e = new ExplosionTile(this, c, r, orient, ownerId);
    this.explosionsGroup.add(e);
  }

  private handlePlayerDeath(p: Player) {
    if (this.spectatorById[p.id]) return;
    AudioManager.sfx('die');
    // Drop collected upgrades at nearby empty tiles so others can pick them up
    this.dropPlayerPowerups(p);
    // Reset stats — player starts fresh on respawn
    p.stats = { ...DEFAULT_STATS };
    // Always auto-respawn after a short pause (infinite lives)
    this.time.delayedCall(RESPAWN_DELAY_MS, () => {
      if (this.spectatorById[p.id] || this.levelEnded) return;
      if (!p.alive) this.tryRespawn(p.id);
    });
  }

  private dropPlayerPowerups(player: Player) {
    const { bombCount, bombRange, speedLevel, hasRemote, hasKick, hasPierce, hasShield } = player.stats;
    const drops: Array<keyof typeof POWERUPS> = [];
    for (let i = 1; i < bombCount; i++) drops.push('bomb');
    for (let i = 1; i < bombRange; i++) drops.push('range');
    for (let i = 0; i < speedLevel; i++) drops.push('speed');
    if (hasRemote) drops.push('remote');
    if (hasKick) drops.push('kick');
    if (hasPierce) drops.push('pierce');
    if (hasShield) drops.push('shield');
    if (drops.length === 0) return;

    // BFS outward from death position to find enough empty tiles
    const startCol = player.gridCol();
    const startRow = player.gridRow();
    const visited = new Set<string>();
    const queue: Array<{ c: number; r: number }> = [{ c: startCol, r: startRow }];
    visited.add(`${startCol},${startRow}`);
    const emptyCells: Array<{ c: number; r: number }> = [];
    const gridRows = this.grid.length;
    const gridCols = this.grid[0]?.length ?? 0;

    while (queue.length > 0 && emptyCells.length < drops.length) {
      const { c, r } = queue.shift()!;
      if (this.grid[r]?.[c] === 'empty') emptyCells.push({ c, r });
      for (const [dc, dr] of [[0,1],[0,-1],[1,0],[-1,0]] as const) {
        const nc = c + dc, nr = r + dr;
        const key = `${nc},${nr}`;
        if (!visited.has(key) && nr >= 0 && nr < gridRows && nc >= 0 && nc < gridCols) {
          visited.add(key);
          queue.push({ c: nc, r: nr });
        }
      }
    }

    drops.forEach((kind, i) => {
      const cell = emptyCells[i];
      if (!cell) return;
      const spec = POWERUPS[kind];
      const pu = new PowerUp(this, cell.c, cell.r, spec);
      this.powerupsGroup.add(pu);
    });
  }

  private continuePlayer(id: PlayerId) {
    this.pendingContinueById[id] = false;
    if (this.levelEnded) return;
    const p = this.players.find(pl => pl.id === id);
    if (!p) return;
    p.lives = CONTINUE_REFILL_LIVES;
    this.spectatorById[id] = false;
    this.tryRespawn(id);
  }

  private becomeSpectator(id: PlayerId) {
    this.pendingContinueById[id] = false;
    this.spectatorById[id] = true;
    // Player remains dead/tinted; no further respawn attempts will fire.
  }

  private snapshotForSave(): GameSceneData {
    return {
      world: this.currentWorld,
      stage: this.currentStage,
      p1: { name: this.players[0].displayName, stats: { ...this.players[0].stats }, lives: this.players[0].lives, score: this.scoresById.p1 },
      p2: { name: this.players[1].displayName, stats: { ...this.players[1].stats }, lives: this.players[1].lives, score: this.scoresById.p2 },
    };
  }

  private completeLevel() {
    this.levelEnded = true;
    // Power-ups carry forward within a world. Only reset when the boss stage (6)
    // is completed — i.e., when transitioning to the next world.
    const snap = this.snapshotForSave();
    // Spectators (or anyone at 0 lives) get a fresh pool on the next level so
    // a single rough level doesn't permanently sideline a player.
    const carryLives = (id: PlayerId, current: number) =>
      (this.spectatorById[id] || current <= 0) ? CONTINUE_REFILL_LIVES : current;
    const resetStats = this.currentStage === 6; // world transition resets upgrades
    const payload: GameSceneData = {
      ...snap,
      p1: { name: snap.p1.name, stats: resetStats ? { ...DEFAULT_STATS } : { ...snap.p1.stats }, lives: carryLives('p1', snap.p1.lives), score: snap.p1.score + 1000 },
      p2: { name: snap.p2.name, stats: resetStats ? { ...DEFAULT_STATS } : { ...snap.p2.stats }, lives: carryLives('p2', snap.p2.lives), score: snap.p2.score + 1000 },
    };
    saveAutosave(payload);
    saveHighScore(Math.max(payload.p1.score, payload.p2.score));
    AudioManager.sfx('level-win');

    const next = getNextLevel(this.currentWorld, this.currentStage);

    const banner = this.add.text(CANVAS_W / 2, CANVAS_H / 2,
      next ? `LUME ${this.currentWorld}-${this.currentStage} COMPLET` : `LUME ${this.currentWorld} TERMINATĂ`,
      { fontFamily: 'monospace', fontSize: '36px', color: '#ffd166', backgroundColor: '#131120', padding: { x: 20, y: 12 } }
    ).setOrigin(0.5).setDepth(2000);
    this.tweens.add({ targets: banner, alpha: { from: 0, to: 1 }, duration: 300 });

    this.time.delayedCall(1800, () => {
      banner.destroy();
      if (!next) {
        // Final boss W10-6 → victory
        this.scene.start('VictoryScene', payload);
        return;
      }
      // Boss kill (stage 6) → world outro → next-world intro → GameScene
      if (this.currentStage === 6 && this.currentWorld < 10) {
        const nextWorldData = { ...payload, world: next.world, stage: next.stage };
        this.scene.start('StoryScene', {
          kind: 'world-outro',
          world: this.currentWorld,
          next: {
            scene: 'StoryScene',
            data: {
              kind: 'world-intro',
              world: next.world,
              next: { scene: 'GameScene', data: nextWorldData },
            },
          },
        });
        return;
      }
      // Normal level transition
      this.scene.start('GameScene', { ...payload, world: next.world, stage: next.stage });
    });
  }

  private gameOver(label: string = 'GAME OVER') {
    this.levelEnded = true;
    clearAutosave();
    // Dismiss any open continue prompts so they don't strand the game state.
    const overlay = this.scene.get('ContinueOverlay') as ContinueOverlay | undefined;
    overlay?.cancelFor('p1');
    overlay?.cancelFor('p2');
    const banner = this.add.text(CANVAS_W / 2, CANVAS_H / 2,
      label,
      { fontFamily: 'monospace', fontSize: '48px', color: '#ef476f', backgroundColor: '#131120', padding: { x: 24, y: 14 } }
    ).setOrigin(0.5).setDepth(2000);
    this.tweens.add({ targets: banner, alpha: { from: 0, to: 1 }, duration: 300 });
    saveHighScore(Math.max(this.scoresById.p1, this.scoresById.p2));
    this.time.delayedCall(2500, () => {
      this.scene.start('MenuScene');
    });
  }

  private spawnBoss() {
    const spec = BOSS_SPECS[this.currentWorld] ?? BOSS_SPECS[10];
    const gridQuery = {
      isBlocked: (col: number, row: number) => {
        if (col < 0 || col >= GRID_W || row < 0 || row >= GRID_H) return true;
        const cell = this.grid[row][col];
        return cell === 'hard' || cell === 'soft' || cell === 'bomb';
      },
    };
    // Find an empty tile near center — some boss arena variants have a pillar at exact center
    const targetCol = Math.floor(GRID_W / 2);
    const targetRow = Math.floor(GRID_H / 2);
    let bossCol = targetCol, bossRow = targetRow;
    if (this.grid[bossRow]?.[bossCol] !== 'empty') {
      outer: for (let dist = 1; dist <= 6; dist++) {
        for (let dr = -dist; dr <= dist; dr++) {
          for (let dc = -dist; dc <= dist; dc++) {
            if (Math.abs(dr) !== dist && Math.abs(dc) !== dist) continue;
            const nc = targetCol + dc, nr = targetRow + dr;
            if (nr >= 1 && nr < GRID_H - 1 && nc >= 1 && nc < GRID_W - 1 && this.grid[nr]?.[nc] === 'empty') {
              bossCol = nc; bossRow = nr;
              break outer;
            }
          }
        }
      }
    }
    const boss = new Boss(
      this, spec, bossCol, bossRow, gridQuery,
      () => this.players.map(p => ({ x: p.x, y: p.y, alive: p.alive })),
      (col, row, kind, dx, dy) => this.handleBossAttack(col, row, kind, dx, dy),
    );
    this.enemiesGroup.add(boss);
    this.enemies.push(boss);
    this.boss = boss;
    this.bossHpBar = this.add.graphics().setDepth(1500).setScrollFactor(0);
    this.drawBossHpBar();

    // Pre-spawn generous loot on boss levels (2 players need plenty of upgrades)
    // Emphasis: extra bombs + range first, then special items
    const bossLoot: Array<keyof typeof POWERUPS> = [
      'bomb', 'bomb', 'bomb',
      'range', 'range', 'range', 'range',
      'remote', 'pierce', 'speed', 'speed',
      'shield', 'shield',
      'bomb', 'range',
    ];
    const lootCandidates: Array<{ c: number; r: number }> = [];
    for (let r = 1; r < GRID_H - 1; r++) {
      for (let c = 1; c < GRID_W - 1; c++) {
        if (this.grid[r][c] !== 'empty') continue;
        const farFromP1 = Math.max(Math.abs(c - 1), Math.abs(r - 1)) >= 3;
        const farFromP2 = Math.max(Math.abs(c - (GRID_W - 2)), Math.abs(r - (GRID_H - 2))) >= 3;
        if (farFromP1 && farFromP2) lootCandidates.push({ c, r });
      }
    }
    Phaser.Utils.Array.Shuffle(lootCandidates);
    bossLoot.forEach((kind, i) => {
      const cell = lootCandidates[i];
      if (!cell) return;
      const pu = new PowerUp(this, cell.c, cell.r, POWERUPS[kind]);
      this.powerupsGroup.add(pu);
    });
  }

  private handleBossAttack(fromCol: number, fromRow: number, kind: BossAttackKind, dx: number, dy: number) {
    AudioManager.sfx('explode');
    const range = this.boss?.spec.attackRange ?? 4;
    const DIRS: Array<[number, number]> = [[dx, dy]];

    if (kind === 'spread') {
      // Add one perpendicular direction
      if (dx === 0) DIRS.push([1, 0]);
      else          DIRS.push([0, 1]);
    }
    if (kind === 'cross' || kind === 'star' || kind === 'burst') {
      DIRS.push([0, 1], [0, -1], [1, 0], [-1, 0]);
    }

    // Deduplicate directions
    const seen = new Set<string>();
    const uniqueDirs = DIRS.filter(([x, y]) => {
      const k = `${x},${y}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });

    // Traveling flame: each tile appears with a 65ms delay per distance step
    uniqueDirs.forEach(([fx, fy]) => {
      for (let i = 1; i <= range; i++) {
        const c = fromCol + fx * i;
        const r = fromRow + fy * i;
        const delay = i * 65;
        this.time.delayedCall(delay, () => {
          if (!this.scene.isActive()) return;
          if (c < 0 || c >= GRID_W || r < 0 || r >= GRID_H) return;
          if (this.grid[r]?.[c] === 'hard') return;
          if (this.grid[r]?.[c] === 'soft') {
            // Boss flames burn through soft blocks (visual only, don't destroy)
            // Just stop the flame here but show explosion
            this.spawnExplosion(c, r, fx === 0 ? 'vertical' : 'horizontal', 'boss');
            return;
          }
          this.spawnExplosion(c, r, fx === 0 ? 'vertical' : 'horizontal', 'boss');
        });
      }
    });

    // 'burst' also fires a second delayed wave for the "double burst" effect
    if (kind === 'burst') {
      uniqueDirs.forEach(([fx, fy]) => {
        for (let i = 1; i <= range; i++) {
          const c = fromCol + fx * i;
          const r = fromRow + fy * i;
          this.time.delayedCall(350 + i * 65, () => {
            if (!this.scene.isActive()) return;
            if (c < 0 || c >= GRID_W || r < 0 || r >= GRID_H) return;
            if (this.grid[r]?.[c] === 'hard' || this.grid[r]?.[c] === 'soft') return;
            this.spawnExplosion(c, r, fx === 0 ? 'vertical' : 'horizontal', 'boss');
          });
        }
      });
    }
  }

  private drawBossHpBar() {
    if (!this.boss || !this.bossHpBar) return;
    const g = this.bossHpBar;
    g.clear();
    const w = CANVAS_W - 80;
    const h = 18;
    const x = 40;
    const y = CANVAS_H - 32;
    g.fillStyle(0x131120, 0.85).fillRect(x - 4, y - 4, w + 8, h + 8);
    g.fillStyle(0x3a2818, 1).fillRect(x, y, w, h);
    const frac = Math.max(0, this.boss.hp / this.boss.spec.hp);
    g.fillStyle(0xef476f, 1).fillRect(x, y, w * frac, h);
    g.lineStyle(2, 0xffd166, 1).strokeRect(x, y, w, h);
  }

  private spawnEnemies(count: number) {
    if (count <= 0) return;
    const candidates: Array<{ col: number; row: number }> = [];
    for (let r = 1; r < GRID_H - 1; r++) {
      for (let c = 1; c < GRID_W - 1; c++) {
        if (this.grid[r][c] !== 'empty') continue;
        const farFromP1 = Math.max(Math.abs(c - 1), Math.abs(r - 1)) >= 5;
        const farFromP2 = Math.max(Math.abs(c - (GRID_W - 2)), Math.abs(r - (GRID_H - 2))) >= 5;
        if (farFromP1 && farFromP2) candidates.push({ col: c, row: r });
      }
    }
    Phaser.Utils.Array.Shuffle(candidates);

    const gridQuery = {
      isBlocked: (col: number, row: number) => {
        if (col < 0 || col >= GRID_W || row < 0 || row >= GRID_H) return true;
        const cell = this.grid[row][col];
        return cell === 'hard' || cell === 'soft' || cell === 'bomb';
      },
    };
    const getPlayers = () => this.players.map(p => ({ x: p.x, y: p.y, alive: p.alive }));
    const getBombs = () => {
      const out: Array<{ col: number; row: number; range: number }> = [];
      for (const b of this.bombsByCell.values()) out.push({ col: b.col, row: b.row, range: b.range });
      return out;
    };

    const kinds = pickEnemyKinds(this.currentWorld, Math.min(count, candidates.length));
    for (let i = 0; i < kinds.length; i++) {
      const { col, row } = candidates[i];
      const kind = kinds[i];
      const e = this.makeEnemy(kind, col, row, gridQuery, getPlayers, getBombs);
      this.enemiesGroup.add(e);
      this.enemies.push(e);
    }
  }

  private makeEnemy(
    kind: EnemyKind,
    col: number, row: number,
    grid: { isBlocked: (c: number, r: number) => boolean },
    getPlayers: () => Array<{ x: number; y: number; alive: boolean }>,
    getBombs: () => Array<{ col: number; row: number; range: number }>,
  ): BaseEnemy {
    switch (kind) {
      case 'tracker':      return new Tracker(this, col, row, grid, getPlayers);
      case 'smarttracker': return new SmartTracker(this, col, row, grid, getPlayers);
      case 'dodger':       return new BombDodger(this, col, row, grid, getBombs);
      case 'wanderer':
      default:             return new Wanderer(this, col, row, grid);
    }
  }

  /**
   * Damage gate: only hurt the player if a meaningful fraction of their body
   * sits inside the flame body. If <40% overlap → they're "behind cover".
   */
  private flameCoverageEnoughToHit(p: Player, flame: Phaser.Physics.Arcade.Sprite): boolean {
    const pb = p.body as Phaser.Physics.Arcade.Body | null;
    const fb = flame.body as Phaser.Physics.Arcade.StaticBody | null;
    if (!pb || !fb) return true; // safe default: hit
    const ox = Math.max(0, Math.min(pb.right, fb.right) - Math.max(pb.left, fb.left));
    const oy = Math.max(0, Math.min(pb.bottom, fb.bottom) - Math.max(pb.top, fb.top));
    const overlap = ox * oy;
    const playerArea = pb.width * pb.height;
    if (playerArea <= 0) return true;
    return (overlap / playerArea) >= 0.40;
  }

  private tryRespawn(id: PlayerId) {
    const p = this.players.find(pl => pl.id === id);
    if (!p || p.alive) return;
    if (this.spectatorById[id]) return;         // spectator never respawns
    // Refuse if spawn point has bomb or explosion currently — retry shortly.
    const cell = this.grid[p.spawnRow]?.[p.spawnCol];
    if (cell === 'bomb') {
      this.time.delayedCall(400, () => this.tryRespawn(id));
      return;
    }
    for (const child of this.explosionsGroup.getChildren()) {
      const e = child as Phaser.Physics.Arcade.Sprite & { col: number; row: number };
      if (e.col === p.spawnCol && e.row === p.spawnRow) {
        this.time.delayedCall(400, () => this.tryRespawn(id));
        return;
      }
    }
    p.respawn();
  }
}
